import React from 'react';

import styled from '@emotion/styled';

import { ErrorCodeEnum, ErrorFragment } from '@graphql/platform';

import { ContactPhoneLink } from '@shared/contact_phone_link';

export type ErrorWithoutTypename = Omit<ErrorFragment, '__typename'>;

type ErrorMetadata = {
  /** Indicates that an error can be resolved by completing an MFA flow (e.g. an existing, canceled account) */
  canResolveViaMFA?: boolean;
};

export class GQLError extends Error {
  public fullError: ErrorWithoutTypename;
  public metadata: ErrorMetadata;
  constructor(error: ErrorWithoutTypename, metadata?: ErrorMetadata) {
    super(error.errorMessage);
    this.fullError = error;
    this.metadata = metadata || {};
  }
}

const LOGIN_LINK = 'https://account.clutter.com';
const LOGIN_DOMAIN = 'account.clutter.com';

function isErrorFragment(data: any): data is ErrorFragment {
  return data.__typename === 'Error';
}

const Link = styled.a`
  text-decoration: underline;
`;

/**
 * Converts text with URLs/domains and special placeholders to React elements with links.
 * - {PHONE} placeholder is replaced with ContactPhoneLink component
 * - account.clutter.com domain is converted to a link
 * - "Clutter Account Portal" text is converted to a link
 */
function linkifyText(text: string): React.ReactElement {
  const parts: React.ReactNode[] = [];
  let remainingText = text;
  let keyCounter = 0;

  // Process text from left to right, finding the earliest match
  while (remainingText.length > 0) {
    const phoneIndex = remainingText.indexOf('{PHONE}');
    const portalIndex = remainingText.indexOf('Clutter Account Portal');
    const domainIndex = remainingText.indexOf(LOGIN_DOMAIN);

    // Find the earliest match
    const indices = [
      phoneIndex >= 0 ? phoneIndex : Infinity,
      portalIndex >= 0 ? portalIndex : Infinity,
      domainIndex >= 0 ? domainIndex : Infinity,
    ];
    const minIndex = Math.min(...indices);

    if (minIndex === Infinity) {
      // No more matches, add remaining text
      if (remainingText.length > 0) {
        parts.push(remainingText);
      }
      break;
    }

    // Add text before the match
    if (minIndex > 0) {
      parts.push(remainingText.slice(0, minIndex));
    }

    // Add the matched component/link
    if (minIndex === phoneIndex) {
      parts.push(<ContactPhoneLink key={`phone-${keyCounter++}`} />);
      remainingText = remainingText.slice(minIndex + '{PHONE}'.length);
    } else if (minIndex === portalIndex) {
      parts.push(
        <Link key={`portal-${keyCounter++}`} href={LOGIN_LINK}>
          Clutter Account Portal
        </Link>,
      );
      remainingText = remainingText.slice(
        minIndex + 'Clutter Account Portal'.length,
      );
    } else if (minIndex === domainIndex) {
      parts.push(
        <Link key={`domain-${keyCounter++}`} href={LOGIN_LINK}>
          {LOGIN_DOMAIN}
        </Link>,
      );
      remainingText = remainingText.slice(minIndex + LOGIN_DOMAIN.length);
    }
  }

  return parts.length > 0 ? <>{parts}</> : <>{text}</>;
}

/**
 * A utility for splitting a union between a custom GQL error and some other
 * type into separate variables, mostly for type safety.
 *
 * @param data The data attribute returned by a GQL query
 */
export function parseGQLErrorUnion<T extends { __typename?: string }>(
  data: ErrorFragment | T | undefined | null,
) {
  const notError =
    data && !isErrorFragment(data)
      ? // TS doesn't narrow T correctly here for some reason
        (data as Exclude<T, ErrorFragment>)
      : undefined;
  const error = data && isErrorFragment(data) ? data : undefined;
  return [notError, error] as const;
}

// Single source of truth for error message text
// Use placeholders: {PHONE} for phone link, {LOGIN_LINK} for account.clutter.com link
const ERROR_MESSAGE_TEXT: PartialRecord<ErrorCodeEnum, string> = {
  [ErrorCodeEnum.PendingStorageOnboarding]:
    'Looks like you already have an appointment with Clutter. Please login to account.clutter.com to make changes to your appointment',
  [ErrorCodeEnum.ActiveStorageAccount]:
    'Looks like you already store items with Clutter. Please login to account.clutter.com to add or remove items.',
  [ErrorCodeEnum.ExistingCustomer]:
    'You already have an account with Clutter. Please visit your Clutter Account Portal to complete booking this appointment.',
  [ErrorCodeEnum.MismatchedCustomer]:
    'A Clutter account for this email address already exists, and has a different phone number on it. Please use the original phone number, or try a new email. For more assistance, call us at {PHONE}.',
  [ErrorCodeEnum.ExistingPendingReferral]:
    'A pending referral already exists on your account. Please try again without the referral code.',
  [ErrorCodeEnum.ExistingPaidReferral]:
    'Your account has already received a referral credit. Please try again without the referral code.',
  [ErrorCodeEnum.PastNextDayCutoff]:
    'Next day appointments cannot be booked past 4:30 PM',
  [ErrorCodeEnum.UnsafeAddress]:
    'This address has been marked as currently unserviceable. If you think this is a mistake, please call us at {PHONE}.',
  [ErrorCodeEnum.BannedAccount]:
    'Your account is disabled and you cannot book appointments. If you think this is a mistake, please call us at {PHONE}.',
  [ErrorCodeEnum.SuspendedAccount]:
    'Your account is suspended. Please pay off your remaining balance before booking an appointment.',
  [ErrorCodeEnum.UnitNotAvailable]:
    'Oh no, that unit is no longer available! Scroll up to check out other units.',
  [ErrorCodeEnum.CouponNotEligible]:
    'That coupon code is not eligible for the service you have selected.',
  [ErrorCodeEnum.ZendeskApiError]:
    'An unexpected error occurred and your callback was not scheduled. Please call us at {PHONE}.',
};

const CUSTOM_MESSAGES: PartialRecord<
  ErrorCodeEnum,
  React.FC<{
    movingSelected?: boolean;
    goToStep?: (stepName: string) => void;
    errorMessage?: string;
  }>
> = {
  [ErrorCodeEnum.PendingStorageOnboarding]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.PendingStorageOnboarding]!),
  [ErrorCodeEnum.ActiveStorageAccount]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.ActiveStorageAccount]!),
  [ErrorCodeEnum.ExistingCustomer]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.ExistingCustomer]!),
  [ErrorCodeEnum.MismatchedCustomer]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.MismatchedCustomer]!),
  [ErrorCodeEnum.ExistingPendingReferral]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.ExistingPendingReferral]!),
  [ErrorCodeEnum.ExistingPaidReferral]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.ExistingPaidReferral]!),
  [ErrorCodeEnum.PastNextDayCutoff]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.PastNextDayCutoff]!),
  [ErrorCodeEnum.UnsafeAddress]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.UnsafeAddress]!),
  [ErrorCodeEnum.BannedAccount]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.BannedAccount]!),
  [ErrorCodeEnum.SuspendedAccount]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.SuspendedAccount]!),
  [ErrorCodeEnum.UnitNotAvailable]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.UnitNotAvailable]!),
  /** Fallback when the API omits `message` on the Error payload. */
  [ErrorCodeEnum.CouponNotEligible]: ({ errorMessage }) =>
    errorMessage?.trim() ? (
      <>{errorMessage}</>
    ) : (
      linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.CouponNotEligible]!)
    ),
  [ErrorCodeEnum.ZendeskApiError]: () =>
    linkifyText(ERROR_MESSAGE_TEXT[ErrorCodeEnum.ZendeskApiError]!),
};

export const FormattedGQLError: React.FC<{
  error: ErrorWithoutTypename;
  movingSelected?: boolean;
  goToStep?: (stepName: string) => void;
}> = ({ error: { errorCode, errorMessage }, movingSelected, goToStep }) => {
  if (errorCode === ErrorCodeEnum.Unknown) {
    // Log unknown error for diagnostics but return safe message to user
    // eslint-disable-next-line no-console
    console.error('Unhandled unknown GQL error:', errorMessage);
    return (
      <>An unexpected error occurred. Please try again or contact support.</>
    );
  }
  const MessageComponent = CUSTOM_MESSAGES[errorCode];
  return (
    <>
      {MessageComponent ? (
        <MessageComponent
          goToStep={goToStep}
          movingSelected={movingSelected}
          errorMessage={errorMessage}
        />
      ) : // If errorMessage looks like a raw class name (contains ::), show a generic message
      errorMessage && errorMessage.includes('::') ? (
        'An unexpected error occurred. Please try again or contact support.'
      ) : (
        errorMessage
      )}
    </>
  );
};
